import { MontoCRC } from '@/components/dominio';
import { VendorPickerBC } from '@/components/filtros/VendorPickerBC';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useMarcarProveedorPactado, useProveedoresPactados } from '@/hooks/useProveedoresPactados';
import type { ProveedorPactado } from '@adelante/shared';
import { CircleNotch, Plus, Trash } from '@phosphor-icons/react';
import { useState } from 'react';
import { toast } from 'sonner';

function Saldo({ p }: { p: ProveedorPactado }) {
  return (
    <>
      <TableCell className="text-right">{p.cantidadFacturas}</TableCell>
      <TableCell className="text-right">
        <MontoCRC monto={p.saldoLCY} />
      </TableCell>
    </>
  );
}

export function ProveedoresPactadosPantalla() {
  const { data, isLoading } = useProveedoresPactados();
  const { marcar, desmarcar } = useMarcarProveedorPactado();
  const [vendorCode, setVendorCode] = useState<string | null>(null);
  const [motivo, setMotivo] = useState('');

  const marcados = data?.data?.marcados ?? [];
  const sugeridos = data?.data?.sugeridos ?? [];

  function marcarProveedor(codigo: string, motivoProveedor: string | null, despues?: () => void) {
    marcar.mutate(
      { vendorCode: codigo, motivo: motivoProveedor },
      {
        onSuccess: () => {
          toast.success('Proveedor marcado como pago pactado');
          despues?.();
        },
        onError: (e) => toast.error(e instanceof Error ? e.message : 'Error al marcar'),
      },
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Proveedores pactados</h1>
        <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
          Proveedores cuyas facturas se van pagando conforme avanza un leasing, un alquiler o un
          crédito negociado. Aunque en Business Central figuren con términos de contado, no los
          maneja Proveeduría: no aparecen en las propuestas de pago (crédito ni contado) y siguen
          visibles en Vencimientos como consulta.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Marcar un proveedor</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap items-center gap-3">
          <VendorPickerBC value={vendorCode} onChange={setVendorCode} />
          <Input
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="Motivo (ej. Leasing, alquiler de maquinaria)"
            maxLength={200}
            className="w-[320px]"
          />
          <Button
            disabled={!vendorCode || marcar.isPending}
            onClick={() =>
              vendorCode &&
              marcarProveedor(vendorCode, motivo.trim() || null, () => {
                setVendorCode(null);
                setMotivo('');
              })
            }
          >
            <Plus className="h-4 w-4" weight="bold" aria-hidden="true" />
            Marcar
          </Button>
        </CardContent>
      </Card>

      {isLoading ? (
        <div className="flex items-center gap-2 text-muted-foreground">
          <CircleNotch className="h-4 w-4 animate-spin" weight="bold" aria-hidden="true" />
          Cargando…
        </div>
      ) : (
        <>
          <Card>
            <CardHeader>
              <CardTitle>Marcados ({marcados.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {marcados.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Todavía no hay proveedores marcados.
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Proveedor</TableHead>
                      <TableHead>Motivo</TableHead>
                      <TableHead className="text-right">Facturas</TableHead>
                      <TableHead className="text-right">Saldo abierto</TableHead>
                      <TableHead className="w-10" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {marcados.map((p) => (
                      <TableRow key={p.vendorCode}>
                        <TableCell className="font-medium">
                          {p.vendorName ?? p.vendorCode}
                          <span className="ml-2 text-xs text-muted-foreground">{p.vendorCode}</span>
                        </TableCell>
                        <TableCell className="text-muted-foreground">{p.motivo ?? '—'}</TableCell>
                        <Saldo p={p} />
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-muted-foreground"
                            title="Quitar la marca: vuelve a las propuestas de pago"
                            onClick={() =>
                              desmarcar.mutate(p.vendorCode, {
                                onSuccess: () => toast.success('Marca quitada'),
                                onError: (e) =>
                                  toast.error(e instanceof Error ? e.message : 'Error al quitar'),
                              })
                            }
                          >
                            <Trash className="h-3.5 w-3.5" weight="bold" aria-hidden="true" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Mayor saldo en contado (sin marcar)</CardTitle>
              <p className="text-sm text-muted-foreground">
                Los proveedores que más pesan hoy en la lista de contado. Si alguno es leasing o un
                crédito negociado, márquelo desde acá.
              </p>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Proveedor</TableHead>
                    <TableHead className="text-right">Facturas</TableHead>
                    <TableHead className="text-right">Saldo abierto</TableHead>
                    <TableHead className="w-24" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sugeridos.map((p) => (
                    <TableRow key={p.vendorCode}>
                      <TableCell className="font-medium">
                        {p.vendorName ?? p.vendorCode}
                        <span className="ml-2 text-xs text-muted-foreground">{p.vendorCode}</span>
                      </TableCell>
                      <Saldo p={p} />
                      <TableCell>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={marcar.isPending}
                          onClick={() => marcarProveedor(p.vendorCode, null)}
                        >
                          Marcar
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
